import React, {useEffect, useState} from 'react';
import Logo from './Logo';
import './LoginForm.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons';
import {useAuth} from './AuthContext';
import {useNavigate} from 'react-router-dom';
import axios from './axiosConfig';
import ForgotPasswordModal from './ForgotPasswordModal';

interface LoginErrorResponse {
    error?: string;
    message?: string;
    ip?: string;
}

const LoginForm: React.FC = () => {
    const [username, setUsername] = useState('');
    const [tenant, setTenant] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    const {
        setIsLoggedIn,
        setUsername: setAuthUsername,
    } = useAuth();

    const navigate = useNavigate();

    useEffect(() => {
        const shouldRemember = localStorage.getItem('rememberMe') === 'true';

        if (shouldRemember) {
            setTenant(localStorage.getItem('tenant') || '');
            setUsername(localStorage.getItem('username') || '');
        }

        setRememberMe(shouldRemember);

        // Remove passwords saved by older versions.
        localStorage.removeItem('password');
    }, []);

    const getLoginErrorMessage = (loginError: unknown): string => {
        if (!axios.isAxiosError<LoginErrorResponse>(loginError)) {
            return 'An unexpected error occurred. Please try again later.';
        }

        if (!loginError.response) {
            return 'Unable to connect to the server. Please check your network connection.';
        }

        const {status, data} = loginError.response;
        const serverMessage = data?.error || data?.message;

        if (status === 400) {
            return serverMessage || 'Please check the information you entered.';
        }

        if (status === 401) {
            return serverMessage || 'Invalid tenant ID, username, or password.';
        }

        if (status === 403) {
            if (data?.ip) {
                return `Access denied. Your IP address (${data.ip}) is not authorized.`;
            }

            return serverMessage || 'Access denied. You are not authorized to sign in.';
        }

        if (status === 429) {
            return 'Too many login attempts. Please try again later.';
        }

        if (status >= 500) {
            return 'The server is temporarily unavailable. Please try again later.';
        }

        return serverMessage || 'Login failed. Please try again.';
    };

    const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            const requestData = {
                tenant: tenant.trim(),
                username: username.trim(),
                password,
            };

            const response = await axios.post('/api/login', requestData);
            const data = response.data;

            localStorage.setItem('token', data.token);
            localStorage.setItem('user_type', data.user_type);

            if (rememberMe) {
                localStorage.setItem('rememberMe', 'true');
                localStorage.setItem('tenant', tenant.trim());
                localStorage.setItem('username', username.trim());
            } else {
                localStorage.removeItem('rememberMe');
                localStorage.removeItem('tenant');
                localStorage.removeItem('username');
            }

            localStorage.removeItem('password');

            setIsLoggedIn(true);
            setAuthUsername(data.username);
            navigate('/');
        } catch (loginError: unknown) {
            console.error('Login failed:', loginError);

            if (axios.isAxiosError<LoginErrorResponse>(loginError)) {
                const status = loginError.response?.status;
                const data = loginError.response?.data;

                if (
                    status === 403 &&
                    data?.error === 'IP address not authorized'
                ) {
                    window.location.reload();
                    return;
                }
            }

            setError(getLoginErrorMessage(loginError));
        } finally {
            setIsSubmitting(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword((currentValue) => !currentValue);
    };

    return (
        <div className="form-container">
            <Logo className="logo-style"/>

            <form onSubmit={handleLogin}>
                <div className="form-field">
                    <label htmlFor="tenant">Tenant ID:</label>
                    <input
                        id="tenant"
                        type="text"
                        value={tenant}
                        onChange={(e) => {
                            setTenant(e.target.value);
                            setError('');
                        }}
                        required
                        autoComplete="organization"
                        onInvalid={(e) => {
                            e.currentTarget.setCustomValidity(
                                'Please enter your Tenant ID',
                            );
                        }}
                        onInput={(e) => {
                            e.currentTarget.setCustomValidity('');
                        }}
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="username">Email or Username:</label>
                    <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value);
                            setError('');
                        }}
                        required
                        autoComplete="username"
                        onInvalid={(e) => {
                            e.currentTarget.setCustomValidity(
                                'Please enter your email or username',
                            );
                        }}
                        onInput={(e) => {
                            e.currentTarget.setCustomValidity('');
                        }}
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="password">Password:</label>

                    <div className="password-container">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setError('');
                            }}
                            required
                            autoComplete="current-password"
                            onInvalid={(e) => {
                                e.currentTarget.setCustomValidity(
                                    'Please enter your password',
                                );
                            }}
                            onInput={(e) => {
                                e.currentTarget.setCustomValidity('');
                            }}
                        />

                        <button
                            type="button"
                            className="toggle-password"
                            onClick={togglePasswordVisibility}
                            aria-label={
                                showPassword
                                    ? 'Hide password'
                                    : 'Show password'
                            }
                            title={
                                showPassword
                                    ? 'Hide password'
                                    : 'Show password'
                            }
                        >
                            <FontAwesomeIcon
                                icon={showPassword ? faEyeSlash : faEye}
                            />
                        </button>
                    </div>
                </div>

                {error && (
                    <p className="error-message" role="alert">
                        {error}
                    </p>
                )}

                <button
                    type="submit"
                    className="login-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Signing in...' : 'Login'}
                </button>

                <div className="form-field d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="me-2"
                        />

                        <label htmlFor="rememberMe" className="mb-0">
                            Remember Me
                        </label>
                    </div>

                    <button
                        type="button"
                        className="forgot-password-link"
                        onClick={() => setShowForgotPassword(true)}
                    >
                        Forgot Password
                    </button>
                </div>
            </form>

            <ForgotPasswordModal
                isOpen={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />
        </div>
    );
};

export default LoginForm;